import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { NzUploadModule, NzUploadFile } from 'ng-zorro-antd/upload';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSplitterModule } from 'ng-zorro-antd/splitter';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';

import { DefinitionControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import { DataTemplatesPreviewComponent } from '../data-templates-preview/data-templates-preview.component';

import {
  AI_SETUP_CONFIG,
  AiSetupSuggestion,
  getAiSetupSuggestions,
  chunkArray,
  buildGenerateLibraryPayload,
  buildSaveUpdatesPayload
} from './ai-setup.config';

type AiSetupState = 'idle' | 'loading' | 'preview' | 'updating' | 'split';

@Component({
  selector: 'rapider-ai-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzUploadModule,
    NzButtonModule,
    NzModalModule,
    NzPopoverModule,
    NzSplitterModule,
    NzInputModule,
    NzCarouselModule,
    DataTemplatesPreviewComponent
  ],
  templateUrl: './ai-setup.component.html',
  styleUrls: ['./ai-setup.component.scss'],
  host: {
    class: 'flex flex-col flex-1 h-full w-full min-h-0 relative bg-body-background overflow-hidden'
  }
})
export class AiSetupComponent {
  private readonly definitionController = inject(DefinitionControllerService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalService = inject(NzModalService);

  public readonly config = AI_SETUP_CONFIG;

  // Computes the massive suggestion list into pages of 4 items for the Carousel
  public readonly suggestions = signal<AiSetupSuggestion[]>(getAiSetupSuggestions());
  public readonly suggestionChunks = computed(() => chunkArray(this.suggestions(), 4));

  // --- State Signals ---
  public uiState = signal<AiSetupState>('idle');
  public promptText = signal<string>('');
  public selectedFiles = signal<File[]>([]);

  // Follow-up Chat State
  public isChatVisible = signal<boolean>(false);
  public chatPromptText = signal<string>('');
  public chatSelectedFiles = signal<File[]>([]);

  // Result Data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public originalDefinition = signal<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updatedDefinition = signal<any>(null);

  // --- Actions ---

  public selectSuggestion(suggestion: AiSetupSuggestion): void {
    this.promptText.set(suggestion.description);
  }

  public beforeUpload = (file: NzUploadFile): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const physicalFile = (file as any).originFileObj || (file as any as File);
    this.selectedFiles.update(files => [...files, physicalFile]);
    return false; // Prevent auto-upload
  };

  public beforeChatUpload = (file: NzUploadFile): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const physicalFile = (file as any).originFileObj || (file as any as File);
    this.chatSelectedFiles.update(files => [...files, physicalFile]);
    return false;
  };

  /**
   * Triggers the primary AI generation. Moves from Idle -> Loading -> Preview.
   */
  public generateModels(): void {
    if (!this.promptText().trim()) return;

    this.uiState.set('loading');

    const payload = buildGenerateLibraryPayload(this.promptText(), this.selectedFiles());

    this.definitionController.generateDataModelTemplateLibrary(payload).subscribe({
      next: (response) => {
        this.originalDefinition.set(response);
        this.uiState.set('preview');
        this.notificationService.createNotification('success', 'Success', this.config.notifications.generateSuccess);
      },
      error: () => {
        this.uiState.set('idle');
        this.notificationService.createNotification('error', 'Error', this.config.notifications.generateError);
      }
    });
  }

  public openChat(): void {
    this.isChatVisible.set(true);
  }

  /**
   * Closes the chat popover without clearing the drafted prompt or files.
   * Resolves the UX issue where clicking outside lost user progress.
   */
  public closeChat(): void {
    this.isChatVisible.set(false);
  }

  /**
   * Triggers a follow-up AI generation using the existing definition ID context.
   * Moves from Preview -> Updating -> Split View.
   */
  public sendUpdatePrompt(): void {
    if (!this.chatPromptText().trim()) return;

    this.isChatVisible.set(false);
    this.uiState.set('updating');

    const originalId = this.originalDefinition().id;
    const payload = buildGenerateLibraryPayload(this.chatPromptText(), this.chatSelectedFiles(), originalId);

    this.definitionController.generateDataModelTemplateLibrary(payload).subscribe({
      next: (response) => {
        this.updatedDefinition.set(response);
        this.uiState.set('split');
        this.notificationService.createNotification('success', 'Success', this.config.notifications.updateSuccess);

        // Reset chat buffers ONLY on successful execution
        this.chatPromptText.set('');
        this.chatSelectedFiles.set([]);
      },
      error: () => {
        this.uiState.set('preview'); // Revert
        this.notificationService.createNotification('error', 'Error', this.config.notifications.updateError);
      }
    });
  }

  /**
   * Patches the definition with the accepted updates and drops out of the Split View.
   */
  public saveUpdates(): void {
    const updatedDef = this.updatedDefinition();
    const originalDef = this.originalDefinition();

    // CRITICAL FIX: Ensure we have both the new definition and the original definition's ID
    if (!updatedDef || !originalDef || !originalDef.id) return;

    const payload = buildSaveUpdatesPayload(originalDef.id, updatedDef);

    // Call update API
    this.definitionController.updateById(payload).subscribe({
      next: () => {
        // Promote the updated version to be the primary original version
        // Ensure the original ID is retained inside the new signal payload so subsequent AI updates work
        this.originalDefinition.set({ ...updatedDef, id: originalDef.id });
        this.updatedDefinition.set(null);
        this.uiState.set('preview');
        this.notificationService.createNotification('success', 'Success', this.config.notifications.savedSuccess);
      },
      error: () => {
        this.notificationService.createNotification('error', 'Error', 'Failed to save changes.');
      }
    });
  }

  /**
   * Prompts for confirmation before discarding the AI's proposed updates.
   */
  public discardUpdates(): void {
    this.modalService.confirm({
      nzTitle: this.config.texts.discardTitle,
      nzContent: this.config.texts.discardMessage,
      nzOkText: 'Discard',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => {
        this.updatedDefinition.set(null);
        this.uiState.set('preview');
        this.notificationService.createNotification('info', 'Discarded', this.config.notifications.discardedSuccess);
      }
    });
  }
}